import type { ReactNode } from 'react'
import { cx } from './cx'
import { toneFill, toneSoft, toneText, type Tone } from './tone'

type DotProps = {
  tone: Tone
  /** Screen-reader text when the dot stands alone without a visible label. */
  label?: string
  size?: 'sm' | 'md'
}

export function Dot({ tone, label, size = 'md' }: DotProps) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cx('inline-block shrink-0 rounded-full', size === 'sm' ? 'size-1.5' : 'size-2', toneFill[tone])}
    />
  )
}

type StatusPillProps = {
  tone: Tone
  children: ReactNode
  /** Leading dot — use in dense lists where the colour is the scan cue. */
  dot?: boolean
  size?: 'sm' | 'md'
}

/** Status as text on a soft tone. Colour alone never carries the meaning — the label always does. */
export function StatusPill({ tone, children, dot, size = 'md' }: StatusPillProps) {
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
        size === 'sm' ? 'h-5 px-2 text-label tracking-normal' : 'h-6 px-2.5 text-small',
        toneSoft[tone],
        toneText[tone],
      )}
    >
      {dot && <Dot tone={tone} size="sm" />}
      {children}
    </span>
  )
}

type BadgeProps = {
  count: number
  max?: number
  tone?: 'ink' | 'accent' | 'negative'
  /** Accessible text, e.g. "3 ungelesene Benachrichtigungen". */
  label?: string
}

const badgeTone = {
  ink: 'bg-ink text-ink-inverse',
  accent: 'bg-accent text-accent-ink',
  negative: 'bg-negative text-ink-inverse',
}

/** Count bubble (notifications, open items). Renders nothing for 0. */
export function Badge({ count, max = 99, tone = 'ink', label }: BadgeProps) {
  if (count <= 0) return null
  return (
    <span
      aria-label={label}
      className={cx(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-label font-semibold tracking-normal tabular',
        badgeTone[tone],
      )}
    >
      {count > max ? `${max}+` : count}
    </span>
  )
}

export type Result = 'S' | 'U' | 'N'

const resultMeta: Record<Result, { tone: Tone; label: string }> = {
  S: { tone: 'positive', label: 'Sieg' },
  U: { tone: 'neutral', label: 'Unentschieden' },
  N: { tone: 'negative', label: 'Niederlage' },
}

type ResultBadgeProps = {
  result: Result
  /** Score or opponent, appended to the accessible name and shown as tooltip ("2:1 gegen SV Tasmania"). */
  detail?: string
  size?: 'sm' | 'md'
}

export function ResultBadge({ result, detail, size = 'md' }: ResultBadgeProps) {
  const { tone, label } = resultMeta[result]
  const name = detail ? `${label}, ${detail}` : label
  return (
    <span
      title={detail ? name : undefined}
      className={cx(
        'inline-grid shrink-0 place-items-center rounded-[6px] font-semibold tabular',
        size === 'sm' ? 'size-5 text-label tracking-normal' : 'size-6 text-small',
        toneSoft[tone],
        toneText[tone],
      )}
    >
      <span aria-hidden="true">{result}</span>
      <span className="sr-only">{name}</span>
    </span>
  )
}

type FormStripProps = {
  /** Oldest first, newest last (reads left → right like a timeline). */
  results: (Result | { result: Result; detail?: string })[]
  size?: 'sm' | 'md'
}

/** Last-5 form. The newest match is underlined so the reading direction is unambiguous. */
export function FormStrip({ results, size = 'md' }: FormStripProps) {
  const items = results.map((r) => (typeof r === 'string' ? { result: r } : r))
  return (
    <ol aria-label={`Form, letzte ${items.length} Spiele, neuestes zuletzt`} className="inline-flex items-center gap-1">
      {items.map((r, i) => (
        <li
          key={i}
          className={cx(
            'relative flex',
            i === items.length - 1 && 'after:absolute after:inset-x-1 after:-bottom-1.5 after:h-0.5 after:rounded-full after:bg-ink-3/50',
          )}
        >
          <ResultBadge result={r.result} size={size} {...(r.detail ? { detail: r.detail } : {})} />
        </li>
      ))}
    </ol>
  )
}
